import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { journalSourceLabel, type GeneralLedgerAccount } from '@accounting/shared';
import { useGeneralLedger } from '../../hooks/useReports';
import { useAccounts } from '../../hooks/useAccounts';
import {
  useBulkMarkReconciled,
  useBulkUnmarkReconciled,
  useDeleteJournalEntry,
} from '../../hooks/useJournalEntries';
import { CONSOLIDATED, useActiveSelection } from '../../stores/useCompanyStore';
import { ExportButtons } from '../../components/reports/ExportButtons';
import { DateRangePicker } from '../../components/reports/PeriodFilter';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { ApiError } from '../../lib/api';
import { formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/reports/general-ledger')({
  // Allow deep-linking to a single account's register (e.g. from the Chart of Accounts).
  validateSearch: (s: Record<string, unknown>): { account?: string; from?: string } => ({
    account: typeof s.account === 'string' ? s.account : undefined,
    from: typeof s.from === 'string' ? s.from : undefined,
  }),
  component: GeneralLedgerPage,
});

const yearStart = () => `${new Date().getFullYear()}-01-01`;

type LineFilter = 'all' | 'cleared' | 'reconciled' | 'neither' | 'cleared_unreconciled';

function GeneralLedgerPage() {
  const selection = useActiveSelection();
  const companyId = selection === CONSOLIDATED ? null : selection;
  const search = Route.useSearch();
  const [from, setFrom] = useState(search.from ?? yearStart());
  const [to, setTo] = useState(todayIso());
  const [accountId, setAccountId] = useState(search.account ?? '');
  const [lineFilter, setLineFilter] = useState<LineFilter>('all');
  const { data: accounts = [] } = useAccounts(companyId);
  const { data, isLoading, error } = useGeneralLedger(companyId, {
    from,
    to,
    accountId: accountId || undefined,
  });

  if (!selection) return <NoCompany />;
  if (selection === CONSOLIDATED) {
    return (
      <div>
        <Link to="/reports" style={{ fontSize: 13 }}>← All reports</Link>
        <div className="card" style={{ textAlign: 'center', padding: 32, marginTop: 8 }}>
          <h1 style={{ marginTop: 0, fontSize: 20 }}>General Ledger</h1>
          <p className="muted">
            The General Ledger is available per company. Pick a single company in the switcher to
            view its ledger.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Link to="/reports" style={{ fontSize: 13 }}>← All reports</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>General Ledger</h1>
        <div style={{ fontSize: 13, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <DateRangePicker from={from} to={to} onChange={(r) => { setFrom(r.from); setTo(r.to); }} />
          <div style={{ minWidth: 260 }}>
            <AccountPicker
              accounts={accounts}
              value={accountId}
              onChange={setAccountId}
              placeholder="All accounts"
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="muted">Show:</span>
            <select
              value={lineFilter}
              onChange={(e) => setLineFilter(e.target.value as LineFilter)}
            >
              <option value="all">All</option>
              <option value="cleared">Cleared (matched to feed)</option>
              <option value="reconciled">Reconciled</option>
              <option value="neither">Neither (open)</option>
              <option value="cleared_unreconciled">Cleared, not reconciled</option>
            </select>
          </label>
          <ExportButtons
            selection={selection}
            type="general_ledger"
            params={accountId ? { from, to, accountId } : { from, to }}
          />
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load report.</p>}

      {data && data.accounts.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 24 }}>
          <p className="muted" style={{ margin: 0 }}>No activity in this range.</p>
        </div>
      )}

      {data?.accounts.map((acct) => (
        // companyId can't be null here — CONSOLIDATED is handled above.
        <AccountBlock
          key={acct.accountId}
          acct={acct}
          companyId={companyId as string}
          lineFilter={lineFilter}
        />
      ))}
    </div>
  );
}

function AccountBlock({
  acct,
  companyId,
  lineFilter,
}: {
  acct: GeneralLedgerAccount;
  companyId: string;
  lineFilter: LineFilter;
}) {
  // Per-account selection — keyed by JE ID. The same JE may appear under
  // multiple accounts (e.g. on both sides of a transfer); selecting in one
  // block doesn't toggle it in another, but the bulk action sends the JE ID
  // regardless so it'll be marked once on the server. That's the right thing.
  const [selected, setSelected] = useState<Record<string, true>>({});
  const selectedIds = Object.keys(selected);
  const mark = useBulkMarkReconciled(companyId);
  const unmark = useBulkUnmarkReconciled(companyId);
  const del = useDeleteJournalEntry(companyId);

  const matchesFilter = (l: GeneralLedgerAccount['lines'][number]) => {
    switch (lineFilter) {
      case 'cleared': return l.isCleared;
      case 'reconciled': return l.isLocked;
      case 'neither': return !l.isCleared && !l.isLocked;
      case 'cleared_unreconciled': return l.isCleared && !l.isLocked;
      default: return true;
    }
  };

  // Client-side amount sort. null = default chronological order from the API.
  // Clicking Debit/Credit headers cycles: off → desc → asc → off. Sorting
  // shuffles the visible rows out of date order, so the running-balance
  // column still shows each row's true balance at its date but the values
  // won't read monotonically — that's expected.
  const [sortBy, setSortBy] = useState<'debit' | 'credit' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = (col: 'debit' | 'credit') => {
    if (sortBy !== col) {
      setSortBy(col);
      setSortDir('desc');
    } else if (sortDir === 'desc') {
      setSortDir('asc');
    } else {
      setSortBy(null);
    }
  };
  const sortIndicator = (col: 'debit' | 'credit') =>
    sortBy === col ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';

  const filteredLines = acct.lines.filter(matchesFilter);
  const visibleLines = sortBy
    ? [...filteredLines].sort((a, b) => {
        const av = parseFloat(sortBy === 'debit' ? a.debit : a.credit);
        const bv = parseFloat(sortBy === 'debit' ? b.debit : b.credit);
        return sortDir === 'desc' ? bv - av : av - bv;
      })
    : filteredLines;

  // Eligible-to-select: anything not already manually-or-formally locked, OR
  // anything that IS manually locked (so we can un-mark). Mixed selections
  // are allowed — the server applies each action where eligible and reports
  // counts. We split the buttons so the user picks intent.
  const unlockedLines = visibleLines.filter((l) => !l.isLocked);
  const manuallyLockedLines = visibleLines.filter(
    (l) => l.isLocked && l.lockedByReconciliationId === null,
  );

  const selectAllUnlocked = () => {
    const next: Record<string, true> = {};
    for (const l of unlockedLines) next[l.journalEntryId] = true;
    setSelected(next);
  };
  const selectAllManuallyLocked = () => {
    const next: Record<string, true> = {};
    for (const l of manuallyLockedLines) next[l.journalEntryId] = true;
    setSelected(next);
  };
  const clearSelection = () => setSelected({});

  const handleMark = () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Mark ${selectedIds.length} entries as reconciled? The matcher will stop suggesting them for new bank transactions.`)) return;
    mark.mutate(selectedIds, {
      onSuccess: (r) => {
        clearSelection();
        const msgs: string[] = [];
        msgs.push(`Marked ${r.marked} as reconciled.`);
        if (r.alreadyLocked > 0) msgs.push(`${r.alreadyLocked} were already locked.`);
        if (r.notFound > 0) msgs.push(`${r.notFound} not found.`);
        alert(msgs.join(' '));
      },
    });
  };
  const handleUnmark = () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Un-mark ${selectedIds.length} entries? Only manually-locked entries are affected — entries closed by a formal reconciliation are left alone.`)) return;
    unmark.mutate(selectedIds, {
      onSuccess: (r) => {
        clearSelection();
        const msgs: string[] = [];
        msgs.push(`Un-marked ${r.unmarked}.`);
        if (r.skippedFormallyReconciled > 0)
          msgs.push(`${r.skippedFormallyReconciled} were locked by an actual reconciliation and were left alone (use reconciliation reversal for those).`);
        if (r.notFound > 0) msgs.push(`${r.notFound} not found.`);
        alert(msgs.join(' '));
      },
    });
  };

  const opError =
    mark.error instanceof ApiError
      ? (mark.error.body as { error?: string } | null)?.error
      : unmark.error instanceof ApiError
        ? (unmark.error.body as { error?: string } | null)?.error
        : del.error instanceof ApiError
          ? (del.error.body as { error?: string } | null)?.error
          : null;
  const inFlight = mark.isPending || unmark.isPending || del.isPending;

  const handleDelete = (l: GeneralLedgerAccount['lines'][number]) => {
    // Escalating confirms: 1 step for plain, 1 stronger warning for cleared
    // (not reconciled), 2 warnings for reconciled. Cancel at any step aborts.
    if (l.isLocked) {
      if (!confirm(
        `WARNING: Entry ${l.entryNumber} is RECONCILED. Deleting it leaves a gap in the reconciled ledger and the linked reconciliation will no longer reference a valid entry.\n\nContinue?`
      )) return;
      if (!confirm(
        `Final confirmation — permanently delete ${l.entryNumber}? This cannot be undone.`
      )) return;
    } else if (l.isCleared) {
      if (!confirm(
        `${l.entryNumber} is matched to a bank-feed transaction. Deleting it will release that bank transaction back to the unmatched feed.\n\nPermanently delete? This cannot be undone.`
      )) return;
    } else {
      if (!confirm(`Permanently delete ${l.entryNumber}? This cannot be undone.`)) return;
    }
    del.mutate(l.journalEntryId);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <h2 style={{ fontSize: 16, margin: 0 }}>
          {acct.code} — {acct.name}
        </h2>
        <div style={{ display: 'flex', gap: 6, fontSize: 12 }}>
          {unlockedLines.length > 0 && (
            <button type="button" className="btn-secondary" onClick={selectAllUnlocked} title="Select every unlocked line on this account in the current range">
              Select all unlocked ({unlockedLines.length})
            </button>
          )}
          {manuallyLockedLines.length > 0 && (
            <button type="button" className="btn-secondary" onClick={selectAllManuallyLocked} title="Select every manually-locked line so you can un-mark them">
              Select manually locked ({manuallyLockedLines.length})
            </button>
          )}
        </div>
      </div>
      {selectedIds.length > 0 && (
        <div
          className="card"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: 8,
            marginBottom: 6,
            background: 'var(--accent-soft, var(--bg-surface))',
            fontSize: 13,
          }}
        >
          <span style={{ fontWeight: 500 }}>{selectedIds.length} selected on this account</span>
          <button type="button" className="btn-primary" onClick={handleMark} disabled={inFlight}>
            {inFlight ? 'Working…' : 'Mark reconciled'}
          </button>
          <button type="button" className="btn-secondary" onClick={handleUnmark} disabled={inFlight}>
            Un-mark reconciled
          </button>
          <button type="button" className="btn-secondary" onClick={clearSelection}>
            Clear
          </button>
        </div>
      )}
      {opError && <div className="field-error" style={{ fontSize: 12, marginBottom: 6 }}>{opError}</div>}
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 32 }}></th>
            <th>Date</th>
            <th>Entry</th>
            <th title="What created this entry: manual, invoice, bank match, import, …">Source</th>
            <th>Memo</th>
            <th
              style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => toggleSort('debit')}
              title="Click to sort by debit (cycles desc → asc → off)"
            >
              Debit{sortIndicator('debit')}
            </th>
            <th
              style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => toggleSort('credit')}
              title="Click to sort by credit (cycles desc → asc → off)"
            >
              Credit{sortIndicator('credit')}
            </th>
            <th style={{ textAlign: 'right' }}>Balance</th>
            <th style={{ width: 130 }}>Status</th>
            <th style={{ width: 1 }}></th>
          </tr>
        </thead>
        <tbody>
          <tr className="muted">
            <td colSpan={7}>Opening balance</td>
            <td style={{ textAlign: 'right' }}>{formatCurrency(acct.openingBalance, true)}</td>
            <td colSpan={2}></td>
          </tr>
          {visibleLines.length === 0 ? (
            <tr>
              <td colSpan={10} className="muted" style={{ textAlign: 'center', padding: 12, fontStyle: 'italic' }}>
                No lines match the current status filter.
              </td>
            </tr>
          ) : visibleLines.map((l, i) => (
            <tr key={`${acct.accountId}-${i}`}>
              <td>
                <input
                  type="checkbox"
                  checked={!!selected[l.journalEntryId]}
                  onChange={() =>
                    setSelected((p) => {
                      const n = { ...p };
                      if (n[l.journalEntryId]) delete n[l.journalEntryId];
                      else n[l.journalEntryId] = true;
                      return n;
                    })
                  }
                />
              </td>
              <td>{formatAccountingDate(l.date)}</td>
              <td>
                <Link to="/journal/$id" params={{ id: l.journalEntryId }}>
                  {l.entryNumber}
                </Link>
              </td>
              <td>
                <span className="tag" style={{ fontSize: 11 }}>{journalSourceLabel(l.source)}</span>
              </td>
              <td>{l.memo ?? ''}</td>
              <td style={{ textAlign: 'right' }}>{l.debit === '0.00' ? '' : formatCurrency(l.debit)}</td>
              <td style={{ textAlign: 'right' }}>{l.credit === '0.00' ? '' : formatCurrency(l.credit)}</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(l.balance, true)}</td>
              <td style={{ fontSize: 12 }}>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {l.isCleared && !l.isLocked && (
                    <span className="tag" title="Matched to a bank-feed transaction but not yet closed by a reconciliation.">
                      cleared
                    </span>
                  )}
                  {l.isLocked && l.lockedByReconciliationId !== null && (
                    <span className="tag ok" title="Locked by a formal reconciliation session — use reconciliation reversal to undo.">
                      reconciled
                    </span>
                  )}
                  {l.isLocked && l.lockedByReconciliationId === null && (
                    <span className="tag" title="Manually marked as reconciled. Reversible from this page.">
                      reconciled*
                    </span>
                  )}
                </div>
              </td>
              <td>
                <button
                  type="button"
                  onClick={() => handleDelete(l)}
                  disabled={inFlight}
                  title={`Permanently delete ${l.entryNumber}`}
                  aria-label={`Delete ${l.entryNumber}`}
                  style={{
                    background: 'none', border: 0, padding: 4, cursor: 'pointer',
                    color: 'var(--text-muted)', fontSize: 14, lineHeight: 1,
                  }}
                >
                  🗑
                </button>
              </td>
            </tr>
          ))}
          <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
            <td colSpan={7}>Ending balance</td>
            <td style={{ textAlign: 'right' }}>{formatCurrency(acct.endingBalance, true)}</td>
            <td colSpan={2}></td>
          </tr>
        </tbody>
      </table>
      {manuallyLockedLines.length > 0 && (
        <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>
          <strong>reconciled*</strong> = manually marked (reversible here). <strong>reconciled</strong> = closed by a reconciliation session (use reconciliation reversal to undo).
        </p>
      )}
    </div>
  );
}

function NoCompany() {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 32 }}>
      <p className="muted">No active company.</p>
      <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
        Go to Companies
      </Link>
    </div>
  );
}
